import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@hero/web-components';
import { PayIcon } from '@hero/web-icons';
import { buttonsColors } from '@hero/web-theme';
import { cn } from '@hero/web-lib';

export interface PayButtonProps {
  /** Button label (e.g. "Pay Now" or JSX with Price component) */
  label: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Loading state */
  isLoading?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Hidden — don't render the button at all */
  hidden?: boolean;
  /** Show the credit-card icon (default: true) */
  showIcon?: boolean;
  /** Full-width button (default: false) */
  fullWidth?: boolean;
  /**
   * Size variant.
   * - `sm`  — compact cell-sized button (table rows)
   * - `md`  — default medium size
   * - `lg`  — large prominent button (wallet card, drawers)
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Cashback percentage as a decimal (e.g. 0.02 for 2%).
   * When provided, displays the percentage and optional days left inline.
   * `sm` shows compact "2%", `md`/`lg` shows "2% cashback".
   */
  cashbackPercentage?: number;
  /**
   * Days left for the cashback offer.
   * Shown as a glass pill next to the percentage.
   */
  cashbackDaysLeft?: number;
  /** Optional className override */
  className?: string;
}

const sizeClasses: Record<NonNullable<PayButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-xs font-medium rounded-lg',
  md: 'h-10 px-5 text-sm font-semibold rounded-lg',
  lg: 'h-12 px-6 text-base font-bold rounded-[32px]',
};

const iconSizeClasses: Record<NonNullable<PayButtonProps['size']>, string> = {
  sm: '!size-3.5',
  md: '!size-4',
  lg: '!size-5',
};

/**
 * PayButton — A high-priority action button used for making payments.
 *
 * Uses the shared "super button" gradient style from @hero/web-theme.
 * Designed to be reused in table cells (sm), drawers (md), and wallet cards (lg).
 *
 * Supports an optional `incentive` line (e.g. "Save 50 SAR") and `urgency`
 * badge (e.g. "2 days left") for early-pay discount promotions.
 *
 * @example
 * ```tsx
 * // Table cell — compact
 * <PayButton label="Pay Now" size="sm" onClick={handlePay} />
 *
 * // Wallet card — large with discount incentive
 * <PayButton
 *   label="Pay Now"
 *   size="lg"
 *   fullWidth
 *   incentive="Save 50 SAR"
 *   urgency="2 days left"
 *   onClick={openPayDrawer}
 * />
 * ```
 */
export const PayButton: React.FC<PayButtonProps> = ({
  label,
  onClick,
  isLoading = false,
  disabled = false,
  hidden = false,
  showIcon = true,
  fullWidth = false,
  size = 'md',
  cashbackPercentage,
  cashbackDaysLeft,
  className,
}) => {
  const { t } = useTranslation('m-payment');
  if (hidden) return null;

  const isCompact = size === 'sm';
  const hasCashback = cashbackPercentage !== undefined && cashbackPercentage > 0;
  const displayPercentage = hasCashback ? Math.round(cashbackPercentage * 100) : 0;

  return (
    <Button
      type="button"
      className={cn(
        buttonsColors.superButtonStyle,
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      disabled={disabled || isLoading}
      onClick={onClick}>
      {/* Shine effect overlay */}
      <span className={buttonsColors.superButtonShine} />

      {/* Content */}
      <span className={cn('relative z-10 flex items-center', isCompact ? 'gap-1.5' : 'gap-2.5')}>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            {showIcon && <PayIcon className={iconSizeClasses[size]} />}
            {label}
            {hasCashback && (
              <>
                <span className={cn('bg-white/30', isCompact ? 'w-px h-3' : 'w-px h-4')} />
                <span className={cn('flex items-center', isCompact ? 'gap-1' : 'gap-1.5')}>
                  <span
                    className={cn(
                      'font-semibold text-emerald-200',
                      isCompact ? 'text-[10px]' : 'text-xs',
                    )}>
                    {isCompact
                      ? `${displayPercentage}%`
                      : t('cashback.cashback_percentage', { percentage: displayPercentage })}
                  </span>
                  {cashbackDaysLeft !== undefined && cashbackDaysLeft >= 0 && (
                    <span className={cn(
                      'rounded-full backdrop-blur-sm',
                      cashbackDaysLeft === 0
                        ? 'bg-red-500/30 text-red-100 animate-pulse'
                        : 'bg-white/15 text-white/80',
                      isCompact ? 'text-[9px] px-1.5 py-px' : 'text-[10px] px-2 py-0.5',
                    )}>
                      {cashbackDaysLeft === 0
                        ? t('cashback.expires_today')
                        : t('cashback.days_left', { count: cashbackDaysLeft })}
                    </span>
                  )}
                </span>
              </>
            )}
          </>
        )}
      </span>
    </Button>
  );
};
